// What each server-sent event changes in the query cache.
//
// Pushed readings go straight into the cached pages that show them (the
// dashboard previews and the readings screen's newest page), so charts move
// without a request. Every other event invalidates exactly the queries it
// concerns, and only those on screen refetch. An event name the client does
// not know invalidates the whole account, as the contract asks.

import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { Channel, ReadingPageLossless, ReadingWithCursor } from '@/api/endpoints';
import { integerToString } from '@/lib/json';
import { queryKeys } from '@/query/keys';
import { PREVIEW_LIMIT } from '@/features/dashboard/previews';
import { mergeNewest, READINGS_PAGE_SIZE } from '@/features/readings/live';
import { MAX_CHART_POINTS } from '@/features/readings/range';
import type { LoadedRange } from '@/features/readings/useChartHistory';

/** A pushed reading: the readings endpoint's shape plus its stream. */
interface PushedReading extends ReadingWithCursor {
  datastreamId: string;
}

/** The readings screen's cached page (its cursor kept as a string). */
interface ScreenPage {
  items: ReadingWithCursor[];
  nextBefore: string | null;
  hasMore: boolean;
}

type Payload = Record<string, unknown>;

function accountQueries(accountId: string, ...path: string[]) {
  return {
    predicate: (query: { queryKey: QueryKey }) => {
      const key = query.queryKey;
      return (
        key[0] === 'account' &&
        key[1] === accountId &&
        path.every((part, index) => key[index + 2] === part)
      );
    },
  };
}

function invalidate(queryClient: QueryClient, accountId: string, ...path: string[]): void {
  void queryClient.invalidateQueries(accountQueries(accountId, ...path));
}

/** Place readings in one cached page, or refetch it when they do not fit. */
function mergeInto<Page extends { items: ReadingWithCursor[]; hasMore: boolean }>(
  queryClient: QueryClient,
  key: QueryKey,
  merge: (page: Page) => Page | null,
): void {
  const page = queryClient.getQueryData<Page>(key);
  // A page nobody has loaded stays unloaded; it is fetched when shown.
  if (!page) return;
  const merged = merge(page);
  if (merged && merged !== page) queryClient.setQueryData(key, merged);
  // A fetch already in flight may have read the page before this reading was
  // committed, and its result would replace the merge: start it again, so the
  // answer includes the reading. A reading that does not fit is refetched too.
  const fetching = queryClient.getQueryState(key)?.fetchStatus === 'fetching';
  if (!merged || fetching) {
    void queryClient.invalidateQueries({ queryKey: key, exact: true }, { cancelRefetch: true });
  }
}

/**
 * Put a pushed reading into every loaded chart range of its datastream.
 *
 * A chart range is a prefix of the history, so a newer reading belongs at its
 * head. A loaded range that already hit the point budget stays complete: the
 * new point replaces the oldest one the range had no room to keep. Ranges not
 * on screen are left alone, so switching preset fetches that range once.
 */
function mergeIntoChart(
  queryClient: QueryClient,
  accountId: string,
  datastreamId: string,
  items: ReadingWithCursor[],
): void {
  const ranges = queryClient.getQueriesData<LoadedRange>({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        key[0] === 'account' &&
        key[1] === accountId &&
        key[2] === 'datastream' &&
        key[3] === datastreamId &&
        key[4] === 'chart'
      );
    },
  });
  for (const [key, range] of ranges) {
    if (!range) continue;
    const known = new Set(range.items.map((item) => integerToString(item.telemetryId)));
    const fresh = items.filter((item) => !known.has(integerToString(item.telemetryId)));
    if (fresh.length === 0) continue;
    const merged = [...fresh, ...range.items];
    // A range that finished loading and did not hit the budget keeps everything
    // it loaded: the new points extend it rather than displacing history.
    const next = range.truncated ? merged.slice(0, MAX_CHART_POINTS) : merged;
    queryClient.setQueryData<LoadedRange>(key, { ...range, items: next });
  }
}

function applyReadings(queryClient: QueryClient, accountId: string, data: Payload): void {
  const sourceId = typeof data.sourceId === 'string' ? data.sourceId : null;
  const readings = Array.isArray(data.readings) ? (data.readings as PushedReading[]) : [];
  if (readings.length === 0) {
    // Too large to push: refetch whatever shows readings.
    invalidate(queryClient, accountId, 'datastream');
    if (sourceId) invalidate(queryClient, accountId, 'source', sourceId, 'channels');
    return;
  }
  const byStream = new Map<string, ReadingWithCursor[]>();
  for (const { datastreamId, ...reading } of readings) {
    byStream.set(datastreamId, [...(byStream.get(datastreamId) ?? []), reading]);
  }
  for (const [datastreamId, items] of byStream) {
    mergeInto<ReadingPageLossless & { hasMore: boolean }>(
      queryClient,
      queryKeys.feedPreview(accountId, datastreamId),
      (page) => mergeNewest(page, items, PREVIEW_LIMIT, (id) => id),
    );
    mergeInto<ScreenPage>(queryClient, queryKeys.readings(accountId, datastreamId, null), (page) =>
      mergeNewest(page, items, READINGS_PAGE_SIZE, integerToString),
    );
    mergeIntoChart(queryClient, accountId, datastreamId, items);
  }
  // A stream the source's channel list does not know yet was just created:
  // the list (and the dashboard built from it) must learn its id.
  if (sourceId) {
    const channels = queryClient.getQueryData<Channel[]>(queryKeys.channels(accountId, sourceId));
    const known = new Set((channels ?? []).map((channel) => channel.datastreamId));
    if (channels && [...byStream.keys()].some((id) => !known.has(id))) {
      invalidate(queryClient, accountId, 'source', sourceId, 'channels');
      invalidate(queryClient, accountId, 'sources');
    }
  }
}

/** Apply one event to the cache. */
export function applyEvent(
  queryClient: QueryClient,
  accountId: string,
  name: string,
  data: Payload,
): void {
  const deviceId = typeof data.deviceId === 'string' ? data.deviceId : null;
  const sourceId = typeof data.sourceId === 'string' ? data.sourceId : null;
  switch (name) {
    case 'readings.new':
      applyReadings(queryClient, accountId, data);
      return;
    case 'configuration.desired':
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'account' &&
          query.queryKey[1] === accountId &&
          query.queryKey.includes('configuration'),
      });
      return;
    case 'device.status':
      if (deviceId) invalidate(queryClient, accountId, 'device', deviceId, 'status');
      invalidate(queryClient, accountId, 'devices');
      return;
    case 'device.capabilities':
      // What the device runs: applied configuration, firmware, its channels.
      if (deviceId) invalidate(queryClient, accountId, 'device', deviceId);
      invalidate(queryClient, accountId, 'devices');
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'account' &&
          query.queryKey[1] === accountId &&
          query.queryKey[2] === 'source' &&
          query.queryKey[4] === 'channels',
      });
      return;
    case 'device.event':
      // OTA progress and Zigbee membership both live under the device.
      if (deviceId) invalidate(queryClient, accountId, 'device', deviceId);
      return;
    case 'source.rejection':
      if (sourceId) invalidate(queryClient, accountId, 'source', sourceId, 'intake');
      return;
    case 'group.changed': {
      // Details, members, sections, invites or viewers: the group with what
      // hangs off it, and the list, which counts members and viewers.
      const groupId = typeof data.groupId === 'string' ? data.groupId : null;
      if (groupId) invalidate(queryClient, accountId, 'group', groupId);
      invalidate(queryClient, accountId, 'groups');
      return;
    }
    default:
      invalidate(queryClient, accountId);
  }
}
